import { CalculatedRole, GROUP_EVAL_ROLES } from '@viapeople/api/src/shared/shared.types'
import {
	ActionRow,
	Button,
	Label,
	ModuleHeader,
	PinIcon,
	resolveCycleStatus,
	TabBar,
	Text,
} from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { GenericTable } from '../../../../admin/src/components/GenericTable'
import { resolveTaskColor, resolveTaskLabel } from '../../utils/helpers'
import { useToast } from '../../utils/ToastContext'
import { MoveEvaluationsModal } from './components/MoveEvaluationBackModal'
import type { CycleStepType } from './cycle-execution/types'
import { useTaskRouter } from './cycle-execution/useTaskRouter'
import { type SearchTasksInput, type Task, useCycleTasksStore } from './stores/cycleTasks.store'

interface Props {
	useStore?: typeof useCycleTasksStore
}

export function CycleTasks({ useStore = useCycleTasksStore }: Props) {
	const navigate = useNavigate()
	const { cycleId } = useParams<{ cycleId: string }>()
	const { handleTaskRouting } = useTaskRouter()
	const mountedRef = useRef(false)
	const { showToast } = useToast()
	const [activeTab, setActiveTab] = useState<CalculatedRole>(CalculatedRole.PRIMARY_MANAGER)
	const [isMoveModalOpen, setIsMoveModalOpen] = useState(false)
	const { cycle, tasks, taskCounts, actions, error, initialize, setSearchTasksInput, clearError } = useStore()

	useEffect(() => {
		if (!mountedRef.current) {
			mountedRef.current = true

			if (cycleId) {
				initialize(cycleId)
			}
		}
	}, [initialize, cycleId])

	useEffect(() => {
		if (error) {
			showToast(error, 'error')
			clearError()
		}
	}, [error, showToast, clearError])

	const formatDeadline = (deadline: string | null) => {
		if (!deadline) return '-'
		const date = new Date(deadline)
		const now = new Date()
		const isOverdue = date < now
		const formattedDate = date.toLocaleDateString('en-US', {
			month: 'short',
			day: 'numeric',
			year: 'numeric',
		})

		return { formattedDate, isOverdue }
	}

	const handleTabChange = (tabId: CalculatedRole) => {
		const roles = tabId === CalculatedRole.RATER ? GROUP_EVAL_ROLES : [tabId]
		setSearchTasksInput({ includeCalculatedRoles: roles })
		setActiveTab(tabId)
	}

	return (
		<main className='w-full p-6'>
			<div className='mb-8'>
				<ModuleHeader
					breadcrumb={[{ label: 'Dashboard', to: '/dashboard' }, { label: cycle?.name || '-' }]}
					title={cycle?.name || '-'}
					logo={{
						url: '',
						name: '',
					}}
				/>
			</div>
			<ActionRow
				headers={['Cycle name', 'Participants', 'Cycle Steps', 'Status']}
				values={[
					cycle.name,
					cycle.participantsCount,
					cycle.cycleSteps,
					<Label
						key='status'
						className='w-25! whitespace-nowrap'
						color={cycle.status === 'in-progress' ? 'yellow' : 'green'}>
						{resolveCycleStatus(cycle.status)}
					</Label>,
				]}
			/>

			{actions?.length && (
				<div className='pt-6'>
					<div>
						<Text variant='large' weight='semibold' className='text-left'>
							Admin Actions
						</Text>
					</div>
					<div className='flex gap-2 mt-2'>
						{actions.includes('searchEvaluations') && (
							<Button variant={'primary'} onClick={() => navigate(`/evaluations/${cycle?.id}`)}>
								Search Evaluations
							</Button>
						)}
						{actions.includes('moveEvaluations') && (
							<Button variant={'primary'} onClick={() => setIsMoveModalOpen(true)}>
								Move Evaluations
							</Button>
						)}
					</div>
				</div>
			)}

			<div className='pt-6'>
				<div>
					<Text variant='large' weight='semibold' className='text-left'>
						Tasks
					</Text>
				</div>

				<div className='border border-neutral-200 rounded-lg'>
					{taskCounts && (
						<div className='border-b border-neutral-100'>
							<TabBar
								tabs={Object.entries(taskCounts).map(([label, count]) => ({
									id:
										(label === 'Manager Evaluation' && CalculatedRole.PRIMARY_MANAGER) ||
										(label === 'Self Evaluation' && CalculatedRole.PARTICIPANT) ||
										(label === 'Rater' && CalculatedRole.RATER) ||
										CalculatedRole.PRIMARY_MANAGER,
									label,
									iconType: 'number',
									iconValue: count?.toString() ?? '0',
								}))}
								activeTab={activeTab}
								onChange={handleTabChange}
							/>
						</div>
					)}

					<GenericTable<Task, SearchTasksInput>
						key={activeTab}
						data={tasks}
						columns={[
							{
								field: 'name',
								label: 'Task name',
								colSpan: 2,
							},
							{
								field: 'participantFirstName',
								label: 'Self Evaluator',
								colSpan: 2,
								render: (task: Task) => (
									<Text variant='xsmall' weight='regular'>
										{task.participantFirstName && task.participantLastName
											? `${task.participantFirstName} ${task.participantLastName}`
											: '-'}
									</Text>
								),
							},
							{
								field: 'deadline',
								label: 'Deadline',
								colSpan: 2,
								render: (task: Task) => {
									const deadline = formatDeadline(task.deadline)
									if (deadline === '-') {
										return (
											<Text variant='xsmall' weight='regular'>
												-
											</Text>
										)
									}
									return (
										<Text variant='xsmall' weight='regular' className={deadline.isOverdue ? 'text-red-600' : ''}>
											{deadline.formattedDate}
										</Text>
									)
								},
							},
							{
								field: 'status',
								label: 'Status',
								colSpan: 2,
								render: (task: Task) => (
									<Label className='w-25!' color={resolveTaskColor(task.status)}>
										{resolveTaskLabel(task.status)}
									</Label>
								),
							},
						]}
						isInternal
						onStatusChange={() => {}}
						onSearchChange={setSearchTasksInput}
						onRowClick={(task) => {
							handleTaskRouting(task.id, task.cycleStepType as CycleStepType)
						}}
						emptyState={{
							icon: PinIcon,
							title: 'No tasks to display',
							description: 'You are up to date!',
						}}
					/>
				</div>
			</div>

			{/* Move Evaluation Back Modal */}
			<MoveEvaluationsModal
				isOpen={isMoveModalOpen}
				onClose={() => {
					setIsMoveModalOpen(false)
					initialize(cycleId as string, activeTab)
				}}
				cycleId={cycleId || ''}
				organizationId={cycle?.organizationId || ''}
			/>
		</main>
	)
}
